import type { BulletDef, EmitterDef, BulletVisual } from '../../content/types';
import { TAU } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { ContentRegistry } from '../../content/registry';
import { World, SIDE_PLAYER, wrapCoord, wrapDelta } from '../world';
import { datan2, dcos, dhypot, dsin } from '../../core/dmath';

export const BULLET_VISUALS: BulletVisual[] = ['orb', 'shard', 'ring', 'needle', 'blob', 'star'];
const visualIndex = new Map<BulletVisual, number>(BULLET_VISUALS.map((v, i) => [v, i]));

export interface SpawnBulletArgs {
  x: number;
  y: number;
  angle: number;
  speed: number;
  def: BulletDef;
  side: number;
  owner: number;
  /** Absolute damage for this bullet. */
  damage: number;
  pierce?: number;
  bounce?: number;
  elements?: number;
  critChance?: number;
  critMult?: number;
  radiusMult?: number;
  stillBonus?: number;
}

/** Allocate one projectile from the pool. Returns slot or 0 if full. */
export function spawnBullet(w: World, reg: ContentRegistry, a: SpawnBulletArgs): number {
  const p = w.projectiles;
  const i = p.pool.alloc();
  if (i === 0) return 0;
  const d = a.def;
  p.x[i] = a.x;
  p.y[i] = a.y;
  p.px[i] = a.x;
  p.py[i] = a.y;
  p.vx[i] = dcos(a.angle) * a.speed;
  p.vy[i] = dsin(a.angle) * a.speed;
  p.heading[i] = a.angle;
  p.accel[i] = d.accel ?? 0;
  p.minSpeed[i] = d.minSpeed ?? 0;
  p.maxSpeed[i] = d.maxSpeed ?? 1e9;
  p.life[i] = d.lifetime;
  p.age[i] = 0;
  p.radius[i] = d.radius * (a.radiusMult ?? 1);
  p.damage[i] = a.damage;
  p.side[i] = a.side;
  p.owner[i] = a.owner;
  p.pierce[i] = a.pierce ?? 0;
  p.bounce[i] = a.bounce ?? 0;
  p.homing[i] = d.homing ?? 0;
  p.homingDelay[i] = d.homingDelay ?? 0;
  p.curve[i] = d.curve ?? 0;
  p.waveAmp[i] = d.wave?.amp ?? 0;
  p.waveFreq[i] = d.wave?.freq ?? 0;
  p.visual[i] = visualIndex.get(d.visual) ?? 0;
  p.color[i] = d.color;
  p.elements[i] = a.elements ?? 0;
  p.critChance[i] = a.critChance ?? 0;
  p.critMult[i] = a.critMult ?? 1;
  p.lastHit[i] = 0;
  p.stillBonus[i] = a.stillBonus ?? 1;
  p.onExpire[i] = d.onExpire ? (reg.patternIndex.get(d.onExpire) ?? -1) : -1;
  p.linger[i] = d.linger ? w.internLinger({ radius: d.linger.radius, duration: d.linger.duration, dps: d.linger.dps, color: d.linger.color ?? d.color }) : -1;
  return i;
}

export interface BurstContext {
  w: World;
  reg: ContentRegistry;
  rng: Rng;
  side: number;
  owner: number;
  damage: number; // absolute damage per bullet (enemy: def.damage; player: computed)
  pierce?: number;
  bounce?: number;
  elements?: number;
  critChance?: number;
  critMult?: number;
  speedMult?: number;
  radiusMult?: number;
  stillBonus?: number;
}

/**
 * Fire one burst of an emitter from (x,y). `aimAngle` is the direction to the
 * target (nearest player for enemies, target enemy for players). `burstIndex`
 * drives spiral rotation. Shared by enemy patterns and the player weapon so
 * the two use identical math.
 */
export function emitBurst(ctx: BurstContext, em: EmitterDef, x: number, y: number, aimAngle: number, burstIndex: number): void {
  const { w, reg, rng } = ctx;
  const count = Math.max(1, em.count | 0);
  const base = (em.angle ?? 0) + (em.rotatePerBurst ?? 0) * burstIndex;
  const aim = em.aim ?? (em.shape === 'aimed' || em.shape === 'wall' || em.shape === 'random');
  const center = aim ? aimAngle + base : base;
  const speedMult = ctx.speedMult ?? 1;
  const offset = em.offset ?? 0;
  const jit = em.speedJitter;

  const fire = (angle: number, sx: number, sy: number) => {
    let speed = em.bullet.speed * speedMult;
    if (jit) speed *= rng.range(jit[0], jit[1]);
    spawnBullet(w, reg, {
      x: sx + dcos(angle) * offset,
      y: sy + dsin(angle) * offset,
      angle,
      speed,
      def: em.bullet,
      side: ctx.side,
      owner: ctx.owner,
      damage: ctx.damage * (ctx.side === SIDE_PLAYER ? em.bullet.damage : 1),
      pierce: ctx.pierce,
      bounce: ctx.bounce,
      elements: ctx.elements,
      critChance: ctx.critChance,
      critMult: ctx.critMult,
      radiusMult: ctx.radiusMult,
      stillBonus: ctx.stillBonus,
    });
  };

  switch (em.shape) {
    case 'aimed': {
      const spread = em.spread ?? 0;
      for (let k = 0; k < count; k++) {
        const t = count === 1 ? 0 : k / (count - 1) - 0.5;
        fire(center + t * spread, x, y);
      }
      break;
    }
    case 'radial':
    case 'spiral': {
      for (let k = 0; k < count; k++) fire(center + (k / count) * TAU, x, y);
      break;
    }
    case 'random': {
      const spread = em.spread ?? TAU;
      for (let k = 0; k < count; k++) fire(center + rng.range(-spread / 2, spread / 2), x, y);
      break;
    }
    case 'wall': {
      const width = em.spread ?? 10;
      const gaps = em.gaps ?? 0;
      // pick gap slots deterministically from the sim rng
      let gapMask = 0;
      for (let g = 0; g < gaps && g < 30; g++) gapMask |= 1 << rng.int(0, Math.min(30, count));
      const nx = -dsin(center);
      const ny = dcos(center);
      for (let k = 0; k < count; k++) {
        if (k < 30 && gapMask & (1 << k)) continue;
        const t = count === 1 ? 0 : (k / (count - 1) - 0.5) * width;
        fire(center, x + nx * t, y + ny * t);
      }
      break;
    }
  }
}

/** Integrate projectiles: motion, homing, curve, wave, expiry, arena bounds. */
export function updateProjectiles(w: World, reg: ContentRegistry, rng: Rng, dt: number, onExpirePattern: (patternIdx: number, x: number, y: number, side: number) => void): void {
  const p = w.projectiles;
  const high = p.pool.high;
  const half = w.half;
  const players = w.players;
  const e = w.enemies;

  for (let i = 1; i < high; i++) {
    if (!p.pool.alive[i]) continue;
    p.px[i] = p.x[i];
    p.py[i] = p.y[i];
    p.age[i] += dt;
    if (p.age[i] >= p.life[i]) {
      expire(w, reg, i, onExpirePattern);
      continue;
    }
    let vx = p.vx[i];
    let vy = p.vy[i];

    // homing: turn toward nearest target of the opposite side
    const hom = p.homing[i];
    if (hom > 0 && p.age[i] >= p.homingDelay[i]) {
      let tx = 0;
      let ty = 0;
      let found = false;
      if (p.side[i] === SIDE_PLAYER) {
        let best = 1e9;
        const eh = e.pool.high;
        for (let j = 1; j < eh; j++) {
          if (!e.pool.alive[j] || Math.abs(e.z[j]) > 0.5) continue;
          const dx = wrapDelta(e.x[j] - p.x[i], half);
          const dy = wrapDelta(e.y[j] - p.y[i], half);
          const d2 = dx * dx + dy * dy;
          if (d2 < best) {
            best = d2;
            tx = p.x[i] + dx;
            ty = p.y[i] + dy;
            found = true;
          }
        }
      } else {
        let best = 1e9;
        for (let j = 0; j < players.length; j++) {
          const pl = players[j];
          if (!pl.alive) continue;
          const dx = wrapDelta(pl.x - p.x[i], half);
          const dy = wrapDelta(pl.y - p.y[i], half);
          const d2 = dx * dx + dy * dy;
          if (d2 < best) {
            best = d2;
            tx = p.x[i] + dx;
            ty = p.y[i] + dy;
            found = true;
          }
        }
      }
      if (found) {
        const speed = dhypot(vx, vy);
        const cur = datan2(vy, vx);
        const want = datan2(ty - p.y[i], tx - p.x[i]);
        let d = want - cur;
        while (d > Math.PI) d -= TAU;
        while (d < -Math.PI) d += TAU;
        const maxTurn = hom * dt;
        const turn = d > maxTurn ? maxTurn : d < -maxTurn ? -maxTurn : d;
        const a = cur + turn;
        vx = dcos(a) * speed;
        vy = dsin(a) * speed;
        p.heading[i] = a;
      }
    }

    // curve: constant angular velocity
    const curve = p.curve[i];
    if (curve !== 0) {
      const c = dcos(curve * dt);
      const s = dsin(curve * dt);
      const nvx = vx * c - vy * s;
      vy = vx * s + vy * c;
      vx = nvx;
    }

    // accel along heading (clamped)
    const acc = p.accel[i];
    if (acc !== 0) {
      let speed = dhypot(vx, vy);
      if (speed > 1e-4) {
        const ns = Math.min(p.maxSpeed[i], Math.max(p.minSpeed[i], speed + acc * dt));
        const f = ns / speed;
        vx *= f;
        vy *= f;
      }
    }
    p.vx[i] = vx;
    p.vy[i] = vy;

    let x = p.x[i] + vx * dt;
    let y = p.y[i] + vy * dt;
    // wave: sideways oscillation
    const amp = p.waveAmp[i];
    if (amp !== 0) {
      const ph = dsin(p.age[i] * p.waveFreq[i] * TAU);
      const speed = dhypot(vx, vy) || 1;
      x += (-vy / speed) * amp * ph * dt;
      y += (vx / speed) * amp * ph * dt;
    }

    // toroidal world: wrap, keeping px/py continuous for interpolation
    const wx = wrapCoord(x, half);
    const wy = wrapCoord(y, half);
    if (wx !== x) p.px[i] += wx - x;
    if (wy !== y) p.py[i] += wy - y;
    p.x[i] = wx;
    p.y[i] = wy;
  }
  if ((w.tick & 31) === 0) p.pool.compactHigh();
}

function expire(w: World, reg: ContentRegistry, i: number, onExpirePattern: (patternIdx: number, x: number, y: number, side: number) => void): void {
  const p = w.projectiles;
  const x = p.x[i];
  const y = p.y[i];
  const side = p.side[i];
  const lg = p.linger[i];
  const def = lg >= 0 ? w.lingerDefs[lg] : undefined;
  if (def) {
    const h = w.hazards;
    const hi = h.pool.alloc();
    if (hi !== 0) {
      h.x[hi] = x;
      h.y[hi] = y;
      h.radius[hi] = def.radius;
      h.life[hi] = def.duration;
      h.maxLife[hi] = def.duration;
      h.dps[hi] = def.dps;
      h.color[hi] = def.color;
      h.side[hi] = side;
      h.armT[hi] = 0;
    }
  }
  const oe = p.onExpire[i];
  p.pool.release(i);
  if (oe >= 0) onExpirePattern(oe, x, y, side);
}

/** Hazards: tick lifetime. Damage is applied in collision.ts. */
export function updateHazards(w: World, dt: number): void {
  const h = w.hazards;
  for (let i = 1; i < h.pool.high; i++) {
    if (!h.pool.alive[i]) continue;
    if (h.armT[i] > 0) h.armT[i] -= dt;
    h.life[i] -= dt;
    if (h.life[i] <= 0) h.pool.release(i);
  }
}
